import React from 'react';
import { ConfigInterface, responseInterface } from 'swr';
import { wpApi, useWPApi, parseUrl } from '@gik/core/api';
import {
  IWordpressCalendarEventType,
  IWordpressCalendarEventTypeOption,
} from '@gik/core/models/wordpress/WordpressCalendarEventType';
import { CalendarEventTypeIds } from '@gik/calendar/models/CalendarEventTypes';
import { WordpressError } from '@gik/core/models/wordpress/WordpressError';
import { apiCap } from '..';

const path = 'cal-event-type';

function getParams() {
  return {
    per_page: apiCap,
  };
}

export function getCalendarEventTypes() {
  const finalUrl = parseUrl(path, {
    searchParams: getParams(),
  });
  return wpApi.get(finalUrl);
}

export function useCalendarEventTypes<Data = IWordpressCalendarEventType[], Error = WordpressError>(
  config?: ConfigInterface
): responseInterface<Data, Error> {
  return useWPApi<Data, Error>(path, config, { searchParams: getParams() });
}

/**
 * Get service categories by event type
 */
export function useCalendarEventTypeOptions(
  hideAnnouncement?: boolean
): Partial<responseInterface<IWordpressCalendarEventTypeOption<number>[], Error>> {
  const { data: events, ...otherProps } = useCalendarEventTypes();
  const [data, setData] = React.useState<IWordpressCalendarEventTypeOption<number>[]>(null);

  React.useEffect(() => {
    if (!events) return;

    let matchingTypes = events.sort(
      (a: IWordpressCalendarEventType, b: IWordpressCalendarEventType) =>
        parseInt(a.acf.sort_order) - parseInt(b.acf.sort_order)
    );

    if (hideAnnouncement) {
      matchingTypes = matchingTypes.filter(item => item.id !== CalendarEventTypeIds.Announcement);
    }

    const options: IWordpressCalendarEventTypeOption<number>[] = matchingTypes?.map(item => ({
      label: item.name,
      slug: item.slug,
      className: 'gik-calendar-event-type--' + item.slug,
      value: item.id,
      image: item.acf.taxonomy_svg_icon,
      variant: item.acf.color_palette,
    }));

    setData(options);
  }, [events, hideAnnouncement, setData]);

  return {
    error: otherProps.error,
    isValidating: otherProps.isValidating,
    revalidate: otherProps.revalidate,
    data,
  };
}

export function getPetCareTypes(eventTypeId: number) {
  const finalUrl = parseUrl(`${path}/${eventTypeId}`);
  return wpApi.get(finalUrl);
}

export function usePetCareTypes(config?: ConfigInterface) {
  return useWPApi<IWordpressCalendarEventType[]>(`${path}/${CalendarEventTypeIds.PetCare}`, config);
}
